import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const Homepage = () => {
  const [decks, setDecks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const navigate = useNavigate();

  const loadDecks = useCallback(async () => {
    try {
      const response = await fetch('https://n8n.automationtekleabsam.com/webhook/load-decks');
      const data = await response.json();

      if (data.success && data.decks) {
        setDecks(data.decks);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Erreur chargement decks:', error);
      setIsLoading(false);
    }
  }, []);

  // Charger les decks au démarrage
  useEffect(() => {
    loadDecks();
  }, [loadDecks]);

  // Synchroniser les decks depuis Notion via n8n
  const syncDecks = async () => {
    setIsSyncing(true);
    try {
      const response = await fetch('https://n8n.automationtekleabsam.com/webhook/sync-decks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success) {
        // Recharger les decks après synchronisation
        await loadDecks();
        alert(`✅ Synchronisation réussie ! ${data.updatedCount || 0} carte(s) mise(s) à jour.`);
      } else {
        alert('❌ Erreur lors de la synchronisation');
      }
    } catch (error) {
      console.error('Erreur synchronisation:', error);
      alert('❌ Erreur de connexion au serveur');
    } finally {
      setIsSyncing(false);
    }
  };

  // Ouvrir un deck pour révision
  const openDeck = (deckId) => {
    navigate(`/review?deck=${deckId}`);
  };

  // Réviser toutes les cartes (tous les decks)
  const reviewAll = () => {
    navigate('/review');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen pb-24 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 mx-auto mb-4 rounded-full border-2 border-sand-300 border-t-clay-500 animate-spin" />
          <div className="label-caps">Chargement des decks</div>
        </div>
      </div>
    );
  }

  const totalCards = decks.reduce((sum, deck) => sum + (deck.totalCards || 0), 0);
  const totalToReview = decks.reduce((sum, deck) => sum + (deck.cardsToReview || 0), 0);
  // Cartes dont l'intervalle atteint 21 jours : le seul indicateur qui atteste
  // d'une mémorisation réelle à long terme.
  const totalMature = decks.reduce((sum, deck) => sum + (deck.matureCards || 0), 0);

  return (
    <div className="min-h-screen pb-28">
      {/* En-tête */}
      <header className="max-w-2xl mx-auto px-5 pt-10 pb-2">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-[2rem] leading-none text-ink-900">
              Tigrigna
            </h1>
            <p className="label-caps mt-2">Révision espacée</p>
          </div>
          <button
            onClick={syncDecks}
            disabled={isSyncing}
            className="p-2 -mr-2 text-ink-400 hover:text-clay-600 disabled:text-sand-300 transition-colors"
            title="Synchroniser depuis Notion"
            aria-label="Synchroniser depuis Notion"
          >
            <svg
              className={`w-5 h-5 ${isSyncing ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              strokeWidth={1.75}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5">
        {decks.length === 0 ? (
          <div className="py-20 text-center max-w-sm mx-auto">
            <h2 className="font-display text-2xl text-ink-900 mb-3">
              Aucun deck
            </h2>
            <p className="text-ink-500 leading-relaxed mb-8">
              Vos decks vivent dans Notion. Synchronisez pour les récupérer ici.
            </p>
            <button
              onClick={syncDecks}
              disabled={isSyncing}
              className="px-6 py-3 bg-clay-500 text-onAccent rounded-xl font-medium hover:bg-clay-600 disabled:bg-sand-300 transition-colors"
            >
              {isSyncing ? 'Synchronisation…' : 'Synchroniser maintenant'}
            </button>
          </div>
        ) : (
          <>
            {/* Le chiffre du jour */}
            <section className="py-8 border-b border-sand-200">
              <div className="label-caps">À réviser aujourd'hui</div>
              <div className="mt-2 flex items-baseline gap-3">
                <span className="font-display text-[4rem] leading-none text-ink-900 tabular-nums">
                  {totalToReview}
                </span>
                <span className="text-ink-500">
                  sur {totalCards} cartes
                </span>
              </div>

              <p className="mt-4 text-sm text-ink-500">
                <span className="tabular-nums font-medium text-ink-900">{totalMature}</span>
                {' '}mémorisée{totalMature > 1 ? 's' : ''} à long terme
                <span className="text-ink-400"> · intervalle ≥ 21 jours</span>
              </p>

              <button
                onClick={reviewAll}
                disabled={totalToReview === 0}
                className="w-full mt-6 py-4 bg-clay-500 text-onAccent rounded-xl font-medium
                           hover:bg-clay-600 disabled:bg-sand-200 disabled:text-ink-400
                           transition-colors shadow-card"
              >
                {totalToReview === 0 ? 'Tout est à jour' : 'Commencer la révision'}
              </button>
            </section>

            {/* Decks */}
            <section className="pt-8">
              <h2 className="label-caps mb-1">
                {decks.length} deck{decks.length > 1 ? 's' : ''}
              </h2>

              <ul className="divide-y divide-sand-200">
                {decks.map((deck) => {
                  const mastery = deck.masteryRate || 0;
                  const due = deck.cardsToReview || 0;

                  return (
                    <li key={deck.id}>
                      <button
                        onClick={() => openDeck(deck.id)}
                        className="w-full text-left py-5 group"
                      >
                        <div className="flex items-baseline justify-between gap-4">
                          <h3 className="font-medium text-ink-900 group-hover:text-clay-600 transition-colors truncate">
                            {deck.name}
                          </h3>
                          {due > 0 && (
                            <span className="shrink-0 label-caps text-clay-600">
                              {due} à réviser
                            </span>
                          )}
                        </div>

                        <div className="mt-2.5 flex items-center gap-3">
                          <div className="flex-1 h-[3px] bg-sand-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-ink-700 rounded-full transition-all duration-500"
                              style={{ width: `${mastery}%` }}
                            />
                          </div>
                          <span className="label-caps tabular-nums shrink-0">
                            {mastery}% · {deck.totalCards || 0} cartes
                          </span>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>

            <button
              onClick={syncDecks}
              disabled={isSyncing}
              className="w-full mt-8 py-3 label-caps hover:text-clay-600 disabled:text-sand-300 transition-colors"
            >
              {isSyncing ? 'Synchronisation en cours…' : 'Synchroniser depuis Notion'}
            </button>
          </>
        )}
      </main>
    </div>
  );
};

export default Homepage;
